import type { AppContext, ViewCleanup } from "../app";
import { loadCountries } from "../data/countries";

// End-of-session review. Shown when the user leaves a learn session having
// missed at least one country. Surfaces every missed country by name and
// lets the user fly the globe to it for one last look before going home.
//
// Per the spec / cognitive-science framing: pure exposure to the right
// shape + name helps consolidation — no scoring or follow-up quiz needed.

export const mountReview = async (
  root: HTMLElement,
  ctx: AppContext,
): Promise<ViewCleanup> => {
  const region = root.dataset.region ?? "your last session";
  const missedIsos = (root.dataset.missedIsos ?? "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);

  root.classList.add("view-review");
  ctx.globe.setTargetIso(null);
  ctx.globe.setGuessIso(null);
  ctx.globe.setHoverEnabled(true);

  const card = document.createElement("section");
  card.className = "review-card";
  card.innerHTML = `
    <header class="review-header">
      <button type="button" class="review-back" aria-label="Back to regions">← Regions</button>
      <h1>Review</h1>
    </header>
    <p class="review-subtitle">Countries you missed in ${region}: ${missedIsos.length}</p>
    <ul class="review-list" role="list"></ul>
    <p class="review-hint">Click a country to fly the globe to it.</p>
  `;
  root.appendChild(card);

  const list = card.querySelector(".review-list") as HTMLUListElement;
  const back = card.querySelector(".review-back") as HTMLButtonElement;

  const table = await loadCountries();
  const items: { btn: HTMLButtonElement; handler: () => void }[] = [];

  for (const iso of missedIsos) {
    const country = table[iso];
    if (!country) continue;
    const li = document.createElement("li");
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "review-item";
    btn.innerHTML = `
      <span class="review-name">${country.canonicalName}</span>
      <span class="review-region">${country.region}</span>
    `;
    const handler = (): void => {
      ctx.globe.setTargetIso(country.iso);
      void ctx.globe.flyToIso(country.iso, { durationMs: 800 });
    };
    btn.addEventListener("click", handler);
    li.appendChild(btn);
    list.appendChild(li);
    items.push({ btn, handler });
  }

  if (items.length === 0) {
    list.innerHTML = `<li class="review-empty">No missed countries to review.</li>`;
  }

  const onBack = (): void => {
    ctx.globe.setTargetIso(null);
    ctx.nav.goRegionPicker();
  };
  back.addEventListener("click", onBack);

  return () => {
    for (const { btn, handler } of items) btn.removeEventListener("click", handler);
    back.removeEventListener("click", onBack);
    ctx.globe.setTargetIso(null);
    root.classList.remove("view-review");
  };
};
